// Login screen — ported from yeni-design/src/screen-login.jsx.
// Real-firm panels (newton, vb.) call /api/auth/login and store JWT;
// demo panels keep the no-args session-flag flow inside AuthContext.
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { I } from '../components/yeni-design/icons';
import { Button, Input, StatusPill } from '../components/yeni-design/primitives';
import { Wordmark } from '../components/yeni-design/Wordmark';

export function LoginPage() {
  const { login, isDemo } = useAuth();
  const navigate = useNavigate();

  const [showPwd, setShowPwd] = useState(false);
  const [email, setEmail] = useState('');
  const [pwd, setPwd] = useState('');
  const [remember, setRemember] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (isDemo) {
        await login();
      } else {
        await login(email.trim(), pwd);
      }
      navigate('/', { replace: true });
    } catch (err) {
      let msg = 'Giriş başarısız. E-posta ve şifrenizi kontrol edin.';
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        if (status === 401 || status === 403) {
          msg = 'E-posta veya şifre hatalı.';
        } else if (status === 429) {
          msg = 'Çok fazla deneme. Lütfen birkaç dakika sonra tekrar deneyin.';
        } else if (status && status >= 500) {
          msg = 'Sunucuya ulaşılamıyor. Lütfen tekrar deneyin.';
        } else if (err.response?.data && typeof err.response.data === 'object') {
          const body = err.response.data as { message?: string; error?: string };
          if (body.message) msg = body.message;
          else if (body.error) msg = body.error;
        }
      } else if (err instanceof Error && err.message) {
        msg = err.message;
      }
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center py-10 px-4 overflow-hidden bg-page">
      {/* subtle radial gradient — primary at 4% in one corner only */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(800px 600px at 100% 0%, rgba(30,64,175,0.08) 0%, rgba(30,64,175,0.02) 35%, rgba(30,64,175,0) 65%)',
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(600px 500px at 0% 100%, rgba(30,64,175,0.04) 0%, transparent 60%)',
        }}
      />

      <div className="relative w-full max-w-[420px]">
        {/* Card */}
        <div className="bg-surface border border-border rounded-xl shadow-md overflow-hidden">
          <form className="px-7 pt-8 pb-7" onSubmit={handleSubmit}>
            <div className="flex justify-center mb-6">
              <Wordmark size="lg" />
            </div>

            <h1 className="serif text-[26px] leading-tight font-semibold text-ink-900 text-center">
              Hesabınıza giriş yapın
            </h1>
            <p className="text-[13px] text-ink-500 text-center mt-2">
              İşletmenizin canlı verilerine güvenli erişim.
            </p>

            <div className="flex flex-col gap-4 mt-7">
              <div>
                <label className="text-[12px] font-semibold text-ink-700 mb-1.5 block">
                  E-posta
                </label>
                <Input
                  icon={I.Mail}
                  type="email"
                  size="lg"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ornek@firma.com.tr"
                  autoComplete="email"
                />
              </div>

              <div>
                <label className="text-[12px] font-semibold text-ink-700 mb-1.5 block">
                  Şifre
                </label>
                <Input
                  icon={I.Lock}
                  type={showPwd ? 'text' : 'password'}
                  size="lg"
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  suffix={
                    <button
                      type="button"
                      onClick={() => setShowPwd((s) => !s)}
                      className="text-ink-400 hover:text-ink-700"
                      aria-label={showPwd ? 'Şifreyi gizle' : 'Şifreyi göster'}
                    >
                      {showPwd ? <I.EyeOff size={14} /> : <I.Eye size={14} />}
                    </button>
                  }
                />
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <span
                    onClick={() => setRemember((r) => !r)}
                    className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                      remember
                        ? 'bg-brand-700 border-brand-700'
                        : 'bg-white border-border-strong'
                    }`}
                  >
                    {remember && (
                      <I.Check size={10} className="text-white" strokeWidth={3} />
                    )}
                  </span>
                  <span className="text-[13px] text-ink-700">Beni hatırla</span>
                </label>
                <a
                  href="#"
                  className="text-[13px] font-medium text-brand-700 hover:text-brand-600"
                  onClick={(e) => e.preventDefault()}
                >
                  Şifremi unuttum
                </a>
              </div>

              {error && (
                <div
                  role="alert"
                  className="text-[12px] text-neg-700 bg-neg-50 border border-neg-100 rounded-md px-3 py-2"
                >
                  {error}
                </div>
              )}

              <Button
                variant="primary"
                size="lg"
                className="w-full mt-1"
                type="submit"
                disabled={submitting}
              >
                {submitting ? 'Giriş yapılıyor…' : 'Giriş Yap'}
              </Button>

              <p className="text-center text-[13px] text-ink-500 mt-1">
                Yeni misiniz?{' '}
                <a
                  href="#"
                  onClick={(e) => e.preventDefault()}
                  className="font-medium text-brand-700 hover:text-brand-600 inline-flex items-center gap-1"
                >
                  Demo talep edin <I.ArrowRight size={11} />
                </a>
              </p>
            </div>
          </form>

          <div className="bg-ink-50 border-t border-border px-7 py-3.5 flex items-center justify-between">
            <StatusPill tone="pos" size="sm">
              Sistem Çevrimiçi
            </StatusPill>
            <span className="text-[11px] text-ink-500 mono">v2.4.1</span>
          </div>
        </div>

        {/* Trust strip below */}
        <div className="mt-5 flex items-center justify-center gap-5 text-[11px] text-ink-500">
          <span className="inline-flex items-center gap-1.5">
            <I.Lock size={11} /> 256-bit SSL şifreleme
          </span>
          <span className="text-ink-300">·</span>
          <span>KVKK uyumlu</span>
          <span className="text-ink-300">·</span>
          <span>ISO 27001</span>
        </div>
      </div>
    </div>
  );
}
